import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Alert, Link } from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthPageFrame from '../components/AuthPageFrame';
import AuthLegalNotice from '../components/AuthLegalNotice';
import SessionReplacementDialog, { SessionReplacementProof, SessionReplacementProofType } from '../components/SessionReplacementDialog';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [replacementChallenge, setReplacementChallenge] = useState<string | null>(null);
  const [replacementProofType, setReplacementProofType] = useState<SessionReplacementProofType>('password');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, confirmSessionReplacement } = useAuth();

  useEffect(() => {
    // CRITICAL: Clear all admin auth when entering the user login page
    // This prevents admin sessions from interfering with non-admin login
    try {
      localStorage.removeItem('admin_auth_token');
      localStorage.removeItem('admin_user');
    } catch (e) {
      // ignore any errors during cleanup
    }

    // Check for success message from location state (e.g., from password reset)
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const submit = async () => {
    setError(null);
    setLoading(true);
    try {
      const userData = await login(email, password);
      setLoading(false);
      finishLogin(userData);
    } catch (err: any) {
      setLoading(false);
      if (err?.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || 'password');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Login failed');
      }
    }
  };

  const finishLogin = (userData: any) => {
    setReplacementChallenge(null);
    const isHighSchool = userData?.educationLevel === 'high-school' ||
      Boolean(userData?.school || userData?.schoolName || userData?.registrationId || userData?.className);
    navigate(isHighSchool ? '/' : '/projects');
  };

  const confirmReplacement = async (proof: SessionReplacementProof) => {
    if (!replacementChallenge) return;
    setLoading(true);
    setError(null);
    try {
      finishLogin(await confirmSessionReplacement(replacementChallenge, proof));
    } catch (err: any) {
      if (err?.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || replacementProofType);
        setError(null);
      } else if (err?.response?.data?.code === 'INVALID_CONFIRMATION_CREDENTIALS') {
        setError(err.response.data.message || 'The password is incorrect.');
      } else if (err?.response?.data?.code === 'SESSION_REPLACEMENT_EXPIRED') {
        setReplacementChallenge(null);
        setError(err.response.data.message || 'The sign-in confirmation expired. Please sign in again.');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Could not sign in on this device');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <AuthPageFrame mode="login">
      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 1.2 }}>
        ACCOUNT ACCESS
      </Typography>
      <Typography component="h1" variant="h4" sx={{ mt: 0.5, mb: 1, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700 }}>
        Sign in
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
        Welcome back. Enter your account details to continue.
      </Typography>
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <TextField label="Email" type="email" autoComplete="email" fullWidth required value={email} onChange={e => setEmail(e.target.value)} sx={{ mb: 2 }} />
        <TextField label="Password" type="password" autoComplete="current-password" fullWidth required value={password} onChange={e => setPassword(e.target.value)} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            sx={{ 
              textDecoration: 'none', 
              fontSize: '0.875rem',
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' } 
            }}
          >
            Forgot Password?
          </Link>
        </Box>
        <Button type="submit" variant="contained" fullWidth size="large" disabled={loading} sx={{ minHeight: 48, textTransform: 'none', fontWeight: 700 }}>
          {loading ? 'Signing in...' : 'Sign in'}
        </Button>
        </Box>
      <Typography variant="body2" sx={{ mt: 2.5, textAlign: 'center' }}>
        New to A3?{' '}
        <Link component={RouterLink} to="/signup" underline="hover" sx={{ fontWeight: 700 }}>
          Create an account
        </Link>
      </Typography>
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <AuthLegalNotice />
      </Box>
    </AuthPageFrame>
    <SessionReplacementDialog
      open={Boolean(replacementChallenge)}
      proofType={replacementProofType}
      loading={loading}
      error={error}
      onConfirm={confirmReplacement}
      onCancel={() => { setReplacementChallenge(null); setError(null); }}
    />
    </>
  );
};

export default Login;
